import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UserPreferencesProvider } from '../contexts/UserPreferencesContext';
import { PricingProvider } from '../contexts/PricingContext';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { MonobankIcon, ABankIcon } from '../components/icons/BankIcons';
import { usePricing } from '../contexts/PricingContext';
import { createTab, getOffer } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getSnapshotFromOffer, sortProductsByPriority, formatProductMetadata } from '../utils/snapshotUtils';
import { exportToPDF, generatePDFFilename } from '../utils/exportUtils';
import { getOfferValidityDate } from '../utils/dateUtils';
import { formatUSDInternalDisplayPrice } from '../utils/currencyUtils';
import { formatSnapshotPrice } from '../utils/pricingItemNormalizer';
import {
  buildCalculatorTemplateUrl,
  buildOfferTemplatePayload,
  canUseOfferAsTemplate,
  getOfferTemplateRegion,
} from '../utils/offerTemplateUtils';

// Landing components
import LandingHeader from '../components/landing/LandingHeader';
import SalesContactCard from '../components/landing/SalesContactCard';
import ProductCard from '../components/landing/ProductCard';
import OneTimePaymentsList from '../components/landing/OneTimePaymentsList';
import AdvantagesSection from '../components/landing/AdvantagesSection';
import ContactFooter from '../components/landing/ContactFooter';

/* --- shared heading helpers --- */
const HEADING_FONT = "'Manrope', sans-serif";
const BODY_FONT = "'Inter', sans-serif";

const SectionTitle = ({ children, decorativeBar = true }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 5 }}>
    <Typography
      variant="h4"
      sx={{
        fontFamily: HEADING_FONT,
        fontWeight: 700,
        fontSize: { xs: '1.75rem', md: '1.875rem' },
        flexShrink: 0,
      }}
    >
      {children}
    </Typography>
    {decorativeBar && (
      <Box
        sx={{
          height: 4,
          flex: 1,
          mx: 4,
          bgcolor: '#edeeef',
          borderRadius: 99,
          display: { xs: 'none', md: 'block' },
        }}
      >
        <Box sx={{ height: '100%', width: '33%', bgcolor: '#00c853', borderRadius: 99 }} />
      </Box>
    )}
  </Box>
);

const OfferPageInner = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { displayCurrency, updatePreferences } = useUserPreferences();
  const { currencyRates } = usePricing();
  const { isAuthenticated } = useAuth();

  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);

  // Set language from offer
  useEffect(() => {
    if (offer?.language && offer.language !== i18n.language) {
      i18n.changeLanguage(offer.language);
    }
  }, [offer?.language, i18n]);

  // Fetch offer data
  useEffect(() => {
    const fetchOffer = async () => {
      try {
        setLoading(true);
        const response = await getOffer(id);
        setOffer(response.data);
      } catch (err) {
        console.error('Error fetching offer:', err);
        setError(err.response?.data?.error || 'Failed to load offer');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchOffer();
    }
  }, [id]);

  const snapshot = getSnapshotFromOffer(offer);

  // Sales contact fallback
  const salesContact = offer?.sales_contact || {
    name: offer?.metadata?.generator_user_name || '',
    email: offer?.metadata?.generator_user_email || '',
    phone: null,
    photo_url: null,
  };

  // Currency-aware price formatter (reacts to displayCurrency toggle).
  // Accepts either a number (aggregate amount) or a normalized snapshot item
  // with `priceCurrency`. formatSnapshotPrice handles legacy fallback for
  // pre-normalization saved offers (phone-numbers stored USD-internal).
  const region = snapshot?.region;
  const rates = snapshot?.currencyRates || currencyRates;
  const fmtPrice = (input) => formatSnapshotPrice(input, { region, displayCurrency, rates });
  const fmtOneTimePrice = (amount) => formatUSDInternalDisplayPrice(amount, region, displayCurrency, rates);

  // Products sorted by display order
  const products = React.useMemo(() => {
    if (!snapshot?.individual_prices) return [];
    return sortProductsByPriority(snapshot.individual_prices).map(item => ({
      ...item,
      description: formatProductMetadata(item, item.productType, t),
    }));
  }, [snapshot, t]);

  // PDF export
  const handleExportPDF = async () => {
    if (!offer) return;
    const validDate = getOfferValidityDate(offer?.created_at);
    const isComp = !!offer?.metadata?.comparison_id;
    const oName = offer?.tab_data?.name || '';
    const filename = generatePDFFilename('offer', oName, clientName, validDate, isComp);
    await exportToPDF('offer-v2-container', filename, 'offer', 1);
  };

  const handleUseAsTemplate = async () => {
    const payload = buildOfferTemplatePayload(offer);
    if (!payload) return;

    const calculatorWindow = window.open('', '_blank');
    setTemplateLoading(true);
    try {
      const response = await createTab(payload);
      const newTabId = response.data?.id;
      const templateRegion = getOfferTemplateRegion(offer);

      if (templateRegion) {
        await updatePreferences({ region: templateRegion });
      }

      if (newTabId) {
        const templateUrl = buildCalculatorTemplateUrl(newTabId);
        if (calculatorWindow) {
          calculatorWindow.opener = null;
          calculatorWindow.location.href = templateUrl;
        } else {
          window.open(templateUrl, '_blank', 'noopener,noreferrer');
        }
      } else if (calculatorWindow) {
        calculatorWindow.close();
      }
    } catch (err) {
      if (calculatorWindow) {
        calculatorWindow.close();
      }
      console.error('Failed to create offer template tab:', err);
    } finally {
      setTemplateLoading(false);
    }
  };

  // Loading
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress sx={{ color: '#00C853' }} />
      </Box>
    );
  }

  // Error
  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography color="error" variant="h6">
          {error}
        </Typography>
      </Container>
    );
  }

  const clientName = offer?.client_name || offer?.metadata?.client_name || '';
  const totalPrice = snapshot?.total_monthly || offer?.total_price || 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', fontFamily: BODY_FONT }}>
      {/* 1. Header */}
      <LandingHeader
        offer={offer}
        onExportPDF={handleExportPDF}
        onUseAsTemplate={isAuthenticated ? handleUseAsTemplate : undefined}
        useAsTemplateDisabled={!canUseOfferAsTemplate(offer)}
        templateLoading={templateLoading}
      />

      <Container id="offer-v2-container" maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>

        {/* 2. Hero + Sales Card — mb: 10 (80px) */}
        <Box
          data-pdf-section="hero"
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            alignItems: { lg: 'stretch' },
            gap: 4,
            mb: 10,
          }}
        >
          {/* Left: Hero (3/5) */}
          <Box
            sx={{
              flex: 3,
              background: 'linear-gradient(160deg, #004d1a 0%, #006e2a 50%, #00c853 100%)',
              color: '#fff',
              p: { xs: 4, md: 5 },
              borderRadius: '1rem',
              boxShadow: '0 12px 40px rgba(25,28,29,0.04)',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
              },
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Top content */}
            <Box>
              {/* Valid until badge */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: 2,
                  py: 0.75,
                  mb: 3,
                  borderRadius: 99,
                  bgcolor: 'rgba(255,255,255,0.25)',
                  color: '#fff',
                  backdropFilter: 'blur(4px)',
                  fontFamily: BODY_FONT,
                  fontSize: '0.875rem',
                  fontWeight: 700,
                }}
              >
                {t('offer.validUntil', 'Valid until {{date}}', { date: getOfferValidityDate(offer?.created_at) })}
              </Box>

              <Typography
                sx={{
                  fontFamily: HEADING_FONT,
                  fontWeight: 800,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  lineHeight: 1.1,
                  letterSpacing: '-0.02em',
                }}
              >
                {(() => {
                  const isStandalone = !offer?.metadata?.comparison_id;
                  const offerName = offer?.tab_data?.name || '';
                  const fullTitle = isStandalone
                    ? t('offers.proposalForCompany', 'Proposal for {{companyName}}', { companyName: clientName })
                    : t('offers.proposalFor', "Proposal '{{offerName}}' for {{companyName}}", { offerName, companyName: clientName });
                  // Highlight client name in green
                  const idx = fullTitle.lastIndexOf(clientName);
                  if (idx >= 0 && clientName) {
                    return <>{fullTitle.slice(0, idx)}<Box component="span" sx={{ color: 'rgba(255,255,255,0.7)' }}>{clientName}</Box>{fullTitle.slice(idx + clientName.length)}</>;
                  }
                  return fullTitle;
                })()}
              </Typography>

              <Typography
                sx={{
                  fontFamily: BODY_FONT,
                  color: 'rgba(255,255,255,0.8)',
                  fontSize: '1.125rem',
                  maxWidth: 560,
                  lineHeight: 1.7,
                }}
              >
                {t('offer.heroDescription', 'A comprehensive communication ecosystem designed to scale with your business and maximize every customer interaction.')}
              </Typography>
            </Box>

            {/* Price row — pushed to bottom */}
            <Box
              sx={{
                mt: 'auto',
                pt: 4,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                <Typography
                  sx={{
                    fontFamily: HEADING_FONT,
                    fontWeight: 800,
                    fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
                    lineHeight: 1,
                    color: '#fff',
                  }}
                >
                  {fmtPrice(totalPrice)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.5rem' },
                    fontWeight: 600,
                    fontFamily: HEADING_FONT,
                    color: 'rgba(255,255,255,0.7)',
                  }}
                >
                  {t('offer.perMonth', '/mo')}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: BODY_FONT,
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: '0.8125rem',
                  mt: 0.5,
                }}
              >
                {t('offer.priceNote', 'annual payment, excl. VAT')}
              </Typography>
            </Box>
          </Box>

          {/* Right: Sales Contact Card (2/5) */}
          <Box sx={{ flex: 2, display: 'flex', justifyContent: { lg: 'flex-end' } }}>
            <SalesContactCard
              salesContact={salesContact}
              variant={isDesktop ? 'full' : 'compact'}
              sx={{ width: '100%', maxWidth: { lg: 380 } }}
            />
          </Box>
        </Box>

        {/* 3. Products Grid — mb: 10, adaptive columns */}
        {products.length > 0 && (() => {
          const count = products.length;
          const cols = count === 1 ? 1 : count === 2 ? 2 : count === 4 ? 2 : 3;
          return (
            <Box data-pdf-section="products" sx={{ mb: 10 }}>
              <SectionTitle>{t('offer.yourProducts', 'Your Products')}</SectionTitle>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: count === 1 ? '1fr' : 'repeat(2, 1fr)',
                  lg: `repeat(${cols}, 1fr)`,
                },
                gridAutoRows: '1fr',
                gap: 3,
                ...(count === 1 && { maxWidth: 600, mx: 'auto' }),
              }}>
                {products.map((product, idx) => (
                  <ProductCard key={`${product.productType}-${idx}`} product={product} formatPrice={fmtPrice} />
                ))}
              </Box>
            </Box>
          );
        })()}

        {/* 4. Included One-time Fees — own section, above Payment Options */}
        {snapshot?.one_time_payments?.length > 0 && (
          <Box data-pdf-section="one-time" sx={{ mb: 10 }}>
            <SectionTitle>{t('offer.includedOneTimeFees', 'Included One-time Fees')}</SectionTitle>
            <OneTimePaymentsList
              payments={snapshot.one_time_payments}
              formatPrice={fmtOneTimePrice}
            />
          </Box>
        )}

        {/* 5. Payment Options — mb: 10, adaptive columns */}
        {snapshot?.subscription_plans?.length > 0 && (() => {
          const planCount = snapshot.subscription_plans.length;
          return (
            <Box data-pdf-section="subscription" sx={{ mb: 10 }}>
              <SectionTitle>{t('offer.subscriptionOptions', 'Payment Options')}</SectionTitle>

              {(() => {
                // 1-2 plans — compact row style
                const hasMultipleOptions = planCount > 1;
                const isUa = snapshot?.region === 'ua';
                const customDiscount360 = snapshot?.custom_discount_360 ?? 20;
                const customDiscount180 = snapshot?.custom_discount_180 ?? 0;

                // Plan label logic — same as SubscriptionPlanDisplay
                const getPlanLabel = (plan) => {
                  if (plan.id === '360-days') {
                    if (customDiscount360 === 0 || !hasMultipleOptions) return t('subscription.360DaysNoBonus', '360 days');
                    return t('subscription.360DaysCustom', '360 days (bonus {{discount}}%)', { discount: customDiscount360 });
                  }
                  if (plan.id === '180-days') {
                    if (customDiscount180 === 0) return t('subscription.180Days', '180 days');
                    return t('subscription.180DaysCustom', '180 days (bonus {{discount}}%)', { discount: customDiscount180 });
                  }
                  const labels = {
                    '90-days': t('subscription.90Days', '90 days'),
                    '12-month-contract': t('subscription.12MonthContract', '12-monthly contract'),
                    '360-days-split': t('subscription.360DaysSplit', '360 days (installment)'),
                  };
                  return labels[plan.id] || plan.id;
                };

                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {snapshot.subscription_plans.map(plan => {
                      // Support both camelCase and snake_case from snapshot
                      const isRec = plan.isRecommended || plan.is_recommended;
                      const planSavings = plan.savings || 0;
                      const showSavings = hasMultipleOptions && planSavings > 0;
                      const payAmount = plan.paymentAmount || plan.payment_amount || 0;
                      const monthPrice = plan.monthlyPrice || plan.monthly_price || 0;
                      const vatAmount = isUa && payAmount ? Math.ceil(payAmount * 1.2) : null;
                      return (
                        <Box key={plan.id} sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          justifyContent: 'space-between',
                          alignItems: { sm: 'flex-start' },
                          gap: { xs: 1.5, sm: 3 },
                          p: 3,
                          bgcolor: 'white',
                          borderRadius: 2,
                          border: isRec && hasMultipleOptions
                            ? '2px solid #006e2a'
                            : '1px solid rgba(187,203,184,0.12)',
                        }}>
                          {/* Left: plan name + badge + monthly price */}
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                              <Typography sx={{ fontFamily: HEADING_FONT, fontWeight: 700, fontSize: '1rem', color: isRec && hasMultipleOptions ? '#006e2a' : 'inherit' }}>
                                {getPlanLabel(plan)}
                              </Typography>
                              {isRec && hasMultipleOptions && (
                                <Box sx={{ px: 1.5, py: 0.25, bgcolor: '#006e2a', borderRadius: 99, display: 'inline-flex' }}>
                                  <Typography sx={{ fontFamily: BODY_FONT, fontSize: '0.6875rem', fontWeight: 700, color: 'white', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    {t('offer.recommended', 'Recommended')}
                                  </Typography>
                                </Box>
                              )}
                              {showSavings && (
                                <Typography sx={{ fontFamily: BODY_FONT, fontSize: '0.8125rem', color: '#006e2a', fontWeight: 600 }}>
                                  {t('subscription.savings', 'Savings')} {fmtPrice(planSavings)}
                                </Typography>
                              )}
                            </Box>
                            {/* Monthly price — secondary */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{ fontFamily: BODY_FONT, fontSize: '1rem', color: '#006e2a', fontWeight: 600 }}>
                                {fmtPrice(monthPrice)}{t('offer.perMonth', '/mo')}
                              </Typography>
                              {plan.id === '360-days-split' && (
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                  <MonobankIcon size={20} />
                                  <ABankIcon size={20} />
                                </Box>
                              )}
                            </Box>
                          </Box>

                          {/* Right: total payment — primary */}
                          {payAmount > 0 && (
                            <Box sx={{ textAlign: { sm: 'right' }, flexShrink: 0 }}>
                              <Typography sx={{ fontFamily: HEADING_FONT, fontWeight: 800, fontSize: '1.5rem', whiteSpace: 'nowrap' }}>
                                {fmtPrice(payAmount)} {t('subscription.toPay', 'to pay')}
                              </Typography>
                              {vatAmount && (
                                <Typography sx={{ fontFamily: BODY_FONT, fontSize: '0.8125rem', color: '#3c4a3c', mt: 0.25 }}>
                                  {t('subscription.vatIncluded', 'Including VAT 20%: {{amount}}', { amount: fmtPrice(payAmount * 1.2) })}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                );
              })()}
            </Box>
          );
        })()}

        {/* 6. Advantages — mb: 10 */}
        <Box data-pdf-section="advantages" sx={{ mb: 10 }}>
          <AdvantagesSection />
        </Box>

        {/* 8. Contact Footer — mb: 10 */}
        <Box data-pdf-section="footer" sx={{ mb: 10 }}>
          <ContactFooter salesContact={salesContact} />
        </Box>

      </Container>
    </Box>
  );
};

const OfferPage = () => {
  return (
    <UserPreferencesProvider controlLanguage={false}>
      <PricingProvider>
        <OfferPageInner />
      </PricingProvider>
    </UserPreferencesProvider>
  );
};

export default OfferPage;
